import { Add } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../../../routes/Routes';
import { listPageTableRowStyling } from '../../../styles/sxStyling';
import { EmailTemplateLink, EmailTemplateStartCondition } from '../../../types/EmailTemplate';
import { UserSettingsCollapseKeys } from '../../../types/User';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import { addOrRemoveDaysToDate } from '../../../utility/dateUtils';
import AccordionSection from '../../AccordionSection';
import { DependencyOption } from '../../tasks/StartCondition';
import PreferencesPopover from '../popover/PreferencesPopover';
import { TemplateNameContent, TemplateSectionModule } from '../Template/TemplateNameContent';
import AddEmailTemplateModal, { ModalAddLinkData } from './AddEmailTemplateModal';
import EmailDataModal from './EmailDataModal/EmailDataModal';
import EmailPreviewModal from './EmailPreviewModal/EmailPreviewModal';

interface IEmailTemplateSectionProps {
  moduleType: TemplateSectionModule;
  linkingModels: EmailTemplateLink[];
  setLinkingModels: (linkingModels: EmailTemplateLink[]) => void;
  collapseId: UserSettingsCollapseKeys;
  startDate?: string;
  availableCustomFields?: string[];
  accountUuid?: string;
  dependencyOptions?: DependencyOption[];
}

export default function EmailTemplatesSection({
  moduleType,
  linkingModels,
  setLinkingModels,
  collapseId,
  startDate,
  availableCustomFields = [],
  accountUuid,
  dependencyOptions = [],
}: IEmailTemplateSectionProps) {
  const { disabled } = useContext(DisableContext);

  const [isEmailTemplateModalOpen, setEmailTemplateModalOpen] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateLink | null>(null);
  const [emailPreviewModalOpen, setEmailPreviewModalOpen] = useState<boolean>(false);
  const [emailDataModalOpen, setEmailDataModalOpen] = useState<boolean>(false);

  const handleAddEmailTemplate: ModalAddLinkData = (linkingData) => {
    if (selectedTemplate) {
      setLinkingModels(
        _.map(linkingModels, (template) => (template.uuid === selectedTemplate.uuid ? { ...linkingData, new_record: template.new_record } : template))
      );
    } else {
      setLinkingModels([...linkingModels, { ...linkingData, new_record: true }]);
    }

    setEmailTemplateModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleRemoveEmailTemplate = (uuid: string) => {
    setLinkingModels(_.reject(linkingModels, { uuid }));
  };

  const handleEditEmailTemplate = (template: EmailTemplateLink) => {
    setSelectedTemplate(template);
    setEmailTemplateModalOpen(true);
  };

  const handlePreviewEmailTemplate = (template: EmailTemplateLink) => {
    setSelectedTemplate(template);
    setEmailPreviewModalOpen(true);
  };

  const handleOpenDataModal = (linkingModel: EmailTemplateLink) => {
    setSelectedTemplate(linkingModel);
    setEmailDataModalOpen(true);
  };

  const title = moduleType === TemplateSectionModule.EVENT_TEMPLATE ? 'Email Templates' : 'Emails';

  // Updated send condition rendering
  // Consolidated send condition + date/offset logic
  const renderSendConditionDisplay = (linkingModel: EmailTemplateLink) => {
    const { start_condition, offset } = linkingModel;

    switch (start_condition) {
      case EmailTemplateStartCondition.MANUAL:
        return 'Manual';
      case EmailTemplateStartCondition.DATE_OFFSET: {
        return `Offset (Days): ${addOrRemoveDaysToDate({ date: startDate, days: offset ?? 0 })}`;
      }
      case EmailTemplateStartCondition.TASK_DEPENDENCY:
        return 'On Task Completion';
      case EmailTemplateStartCondition.EVENT_DEPENDENCY:
        return 'On Event Completion';
      default:
        return '-';
    }
  };

  const emailTemplateTableHeaderActions = (
    <ResponsiveButton
      startIcon={<Add />}
      variant="outlined"
      disabled={disabled}
      onClick={() => {
        setSelectedTemplate(null);
        setEmailTemplateModalOpen(true);
      }}
    >
      Add
    </ResponsiveButton>
  );

  const emailTemplateTableRows = _.map(linkingModels, (linkingModel: EmailTemplateLink) => {
    const hasInvalidFields =
      linkingModel.email_template_custom_fields && !_.every(linkingModel.email_template_custom_fields, (field) => _.includes(availableCustomFields, field));

    return (
      <TableRow key={linkingModel.uuid} sx={{ ...listPageTableRowStyling }}>
        <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasInvalidFields && (
            <Tooltip title="Template uses unavailable custom fields" arrow enterTouchDelay={0} leaveTouchDelay={3000} style={{ cursor: 'pointer' }}>
              <WarningAmberIcon color="warning" />
            </Tooltip>
          )}
          <TemplateNameContent
            moduleType={moduleType}
            isNew={linkingModel.new_record}
            name={linkingModel.email_template_name}
            url={`/${ROUTE_PATHS.EMAIL_TEMPLATES}/${linkingModel.email_template_uuid}`}
            onClick={() => handleOpenDataModal(linkingModel)}
          />
        </TableCell>
        <TableCell>{renderSendConditionDisplay(linkingModel)}</TableCell>
        <TableCell>
          <PreferencesPopover values={linkingModel.roles_values} />
        </TableCell>
        <TableCell>{linkingModel.email_template_subject}</TableCell>
        <TableCell sx={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1 }}>
          {moduleType === TemplateSectionModule.EVENT && accountUuid && !linkingModel.new_record && (
            <IconButton onClick={() => handlePreviewEmailTemplate(linkingModel)}>
              <VisibilityIcon />
            </IconButton>
          )}
          {!disabled && (
            <>
              <IconButton onClick={() => handleEditEmailTemplate(linkingModel)}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleRemoveEmailTemplate(linkingModel.uuid)}>
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </TableCell>
      </TableRow>
    );
  });

  return (
    <>
      <AccordionSection
        title={title}
        headerElements={emailTemplateTableHeaderActions}
        collapseId={collapseId}
        hasData={linkingModels.length > 0}
        scrollable
        id="emails"
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email Template Name</TableCell>
                <TableCell>Send Condition</TableCell>
                <TableCell>Recipient Role</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{emailTemplateTableRows}</TableBody>
          </Table>
        </TableContainer>
      </AccordionSection>

      <AddEmailTemplateModal
        isOpen={isEmailTemplateModalOpen}
        onCancel={() => {
          setEmailTemplateModalOpen(false);
          setSelectedTemplate(null);
        }}
        onAdd={handleAddEmailTemplate}
        emailTemplateLink={selectedTemplate}
        dependencyOptions={dependencyOptions}
      />

      {selectedTemplate && accountUuid && moduleType === TemplateSectionModule.EVENT && (
        <EmailPreviewModal
          selectedTemplate={selectedTemplate}
          accountUuid={accountUuid}
          open={emailPreviewModalOpen}
          onClose={() => {
            setEmailPreviewModalOpen(false);
            setSelectedTemplate(null);
          }}
        />
      )}

      <EmailDataModal
        selectedTemplate={selectedTemplate}
        open={emailDataModalOpen}
        onClose={() => {
          setEmailDataModalOpen(false);
          setSelectedTemplate(null);
        }}
      />
    </>
  );
}
